/**
 * Lista de quadros (matrizes) da rede: quais unidades já têm quadro, de que
 * mês/ano, e quais estão vencidas — com a ação de enviar/atualizar na própria
 * linha. É a tela de partida da Matriz da Unidade: um combobox esconde o que o
 * gestor está tentando descobrir (quem tem quadro), então aqui a lista é o
 * conteúdo e a busca só filtra.
 */
import { UNIDADES, competenciaMatriz, mesesDesde, statusMatriz } from "@caint/data";
import type { StatusMatriz } from "@caint/data";
import type { ResumoMatrizUnidade, Unidade } from "@caint/shared";
import { useMemo, useState } from "react";

export const COR_STATUS: Record<StatusMatriz, string> = {
  atualizado: "var(--success)",
  desatualizado: "var(--warning)",
  ausente: "var(--text-faint)",
};

const ROTULO_STATUS: Record<StatusMatriz, string> = {
  atualizado: "Atualizado",
  desatualizado: "Desatualizado",
  ausente: "Sem quadro",
};

/**
 * Selo do quadro: cor + texto + mês/ano. Nunca só cor — quem não distingue
 * verde de âmbar precisa ler a mesma informação.
 */
export function SeloQuadro({
  resumo,
  agora,
  compacto = false,
}: {
  resumo: Pick<ResumoMatrizUnidade, "atualizadoEm"> | undefined;
  agora: Date;
  compacto?: boolean;
}) {
  const status = statusMatriz(resumo?.atualizadoEm, agora);
  const cor = COR_STATUS[status];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: compacto ? "2px 8px" : "4px 10px",
        borderRadius: 999,
        background: `color-mix(in srgb, ${cor} 14%, transparent)`,
        color: cor,
        fontSize: "var(--text-2xs)",
        fontWeight: 700,
        whiteSpace: "nowrap",
      }}
    >
      <span aria-hidden="true">{status === "ausente" ? "○" : "●"}</span>
      {resumo ? competenciaMatriz(resumo.atualizadoEm) : ROTULO_STATUS.ausente}
    </span>
  );
}

/** Idade do quadro em palavras ("há 14 meses"), para decidir se precisa refazer. */
function idade(iso: string, agora: Date): string {
  const meses = mesesDesde(iso, agora);
  if (meses === null) {
    return "";
  }
  if (meses === 0) {
    return "neste mês";
  }
  return meses === 1 ? "há 1 mês" : `há ${meses} meses`;
}

type Filtro = "todas" | "com" | "vencidos" | "sem";

interface Linha {
  unidade: Unidade;
  resumo: ResumoMatrizUnidade | undefined;
}

function ordenar(linhas: Linha[]): { com: Linha[]; sem: Linha[] } {
  // Com quadro: mais VELHO primeiro — a lista já é a fila de trabalho.
  const com = linhas
    .filter((l) => l.resumo)
    .sort((a, b) => (a.resumo?.atualizadoEm ?? "").localeCompare(b.resumo?.atualizadoEm ?? ""));
  const sem = linhas.filter((l) => !l.resumo).sort((a, b) => a.unidade.nome.localeCompare(b.unidade.nome));
  return { com, sem };
}

function LinhaQuadro({
  linha,
  agora,
  onEscolher,
}: {
  linha: Linha;
  agora: Date;
  onEscolher: (unidadeId: string) => void;
}) {
  const { unidade: u, resumo } = linha;
  return (
    <button type="button" className="m-row" onClick={() => onEscolher(u.id)}>
      <span className="r-main">
        <span className="r-title">{u.nome}</span>
        <span className="r-sub">
          {u.id}
          {u.municipio ? ` · ${u.municipio}` : ""}
          {resumo
            ? ` · ${resumo.cargos} cargos · ${resumo.esperados} esperados · ${idade(resumo.atualizadoEm, agora)}`
            : " · nenhum quadro enviado"}
        </span>
      </span>
      <SeloQuadro resumo={resumo} agora={agora} compacto />
      <span aria-hidden="true" style={{ color: "var(--slate-300)", fontWeight: 700 }}>
        ›
      </span>
    </button>
  );
}

function Secao({
  titulo,
  linhas,
  agora,
  onEscolher,
}: {
  titulo: string;
  linhas: Linha[];
  agora: Date;
  onEscolher: (unidadeId: string) => void;
}) {
  return (
    <>
      <div className="spec" style={{ fontWeight: 700, margin: "14px 0 6px" }}>
        {titulo}
      </div>
      <div className="m-list">
        {linhas.map((l) => (
          <LinhaQuadro key={l.unidade.id} linha={l} agora={agora} onEscolher={onEscolher} />
        ))}
      </div>
    </>
  );
}

export function ListaQuadros({
  resumos,
  agora,
  onEscolher,
}: {
  resumos: readonly ResumoMatrizUnidade[];
  agora: Date;
  onEscolher: (unidadeId: string) => void;
}) {
  const [busca, setBusca] = useState("");
  const [filtro, setFiltro] = useState<Filtro>("todas");
  const [verSem, setVerSem] = useState(false);

  const porUnidade = useMemo(() => new Map(resumos.map((r) => [r.unidadeId, r])), [resumos]);
  const todas = useMemo<Linha[]>(
    () => UNIDADES.map((unidade) => ({ unidade, resumo: porUnidade.get(unidade.id) })),
    [porUnidade],
  );

  const contagem = useMemo(() => {
    const vencidos = resumos.filter((r) => statusMatriz(r.atualizadoEm, agora) === "desatualizado").length;
    return { com: resumos.length, vencidos, sem: UNIDADES.length - resumos.length };
  }, [resumos, agora]);

  const filtradas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return todas.filter((l) => {
      const casa =
        !q || `${l.unidade.id} ${l.unidade.nome} ${l.unidade.municipio ?? ""}`.toLowerCase().includes(q);
      if (!casa) {
        return false;
      }
      if (filtro === "com") {
        return Boolean(l.resumo);
      }
      if (filtro === "sem") {
        return !l.resumo;
      }
      if (filtro === "vencidos") {
        return statusMatriz(l.resumo?.atualizadoEm, agora) === "desatualizado";
      }
      return true;
    });
  }, [todas, busca, filtro, agora]);

  const { com, sem } = ordenar(filtradas);
  // Sem busca e sem filtro, as ~100 unidades sem quadro empurrariam para fora da
  // tela justamente o que importa; ficam atrás de um clique.
  const semEscondidas = filtro === "todas" && !busca.trim() && !verSem && com.length > 0;

  const opcoes: { chave: Filtro; rotulo: string }[] = [
    { chave: "todas", rotulo: `Todas (${UNIDADES.length})` },
    { chave: "com", rotulo: `Com quadro (${contagem.com})` },
    { chave: "vencidos", rotulo: `Desatualizados (${contagem.vencidos})` },
    { chave: "sem", rotulo: `Sem quadro (${contagem.sem})` },
  ];

  return (
    <div>
      <input
        className="m-input"
        style={{ width: "100%" }}
        value={busca}
        placeholder="Buscar unidade por nome, município ou nº…"
        autoComplete="off"
        spellCheck={false}
        onChange={(e) => setBusca(e.target.value)}
      />
      <div className="m-seg" style={{ marginTop: 8, flexWrap: "wrap" }}>
        {opcoes.map((o) => (
          <button
            key={o.chave}
            type="button"
            className={filtro === o.chave ? "on" : ""}
            onClick={() => setFiltro(o.chave)}
          >
            {o.rotulo}
          </button>
        ))}
      </div>

      {com.length > 0 && (
        <Secao
          titulo={`Quadros no sistema — mais antigos primeiro (${com.length})`}
          linhas={com}
          agora={agora}
          onEscolher={onEscolher}
        />
      )}

      {sem.length > 0 &&
        (semEscondidas ? (
          <button
            type="button"
            className="m-btn"
            style={{ marginTop: 12, width: "100%" }}
            onClick={() => setVerSem(true)}
          >
            Mostrar {sem.length} unidade(s) sem quadro
          </button>
        ) : (
          <Secao titulo={`Sem quadro (${sem.length})`} linhas={sem} agora={agora} onEscolher={onEscolher} />
        ))}

      {com.length === 0 && sem.length === 0 && (
        <p className="spec" style={{ marginTop: 14 }}>
          Nenhuma unidade encontrada com esse filtro.
        </p>
      )}
    </div>
  );
}
